import React, { useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  Paper,
  useTheme,
  Fade,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import MapIcon from '@mui/icons-material/Map';
import AOS from 'aos';
import 'aos/dist/aos.css';

const GOOGLE_MAPS_URL = 'https://maps.app.goo.gl/UZV6xsonCXBaZEmE8';

const contactInfo = [
  {
    icon: <EmailIcon />,
    label: 'Email',
    value: 'contact@bixbite.ai',
    href: 'mailto:contact@bixbite.ai',
  },
  {
    icon: <PhoneIcon />,
    label: 'Phone',
    value: '+91 9538474567',
    href: 'tel:+919538474567',
  },
  {
    icon: <LocationOnIcon />,
    label: 'Address',
    value: 'Cohort Coworking, Kondapur, Hyderabad',
    href: GOOGLE_MAPS_URL,
  },
];

const Contact = () => {
  const theme = useTheme();

  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      easing: 'ease-out-cubic',
      disable: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    });
  }, []);

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 5 },
          borderRadius: '28px',
          border: '1px solid rgba(0, 121, 107, 0.1)',
          bgcolor: 'white',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* Background Decoration */}
        <Box
          sx={{
            position: 'absolute',
            top: -120,
            right: -120,
            width: 320,
            height: 320,
            borderRadius: '50%',
            background: 'rgba(0, 121, 107, 0.04)',
            filter: 'blur(60px)',
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -140,
            left: -120,
            width: 320,
            height: 320,
            borderRadius: '50%',
            background: 'rgba(0, 121, 107, 0.04)',
            filter: 'blur(60px)',
            pointerEvents: 'none',
          }}
        />

        <Box sx={{ position: 'relative' }}>
          {/* Header */}
          <Fade in timeout={600}>
            <Box sx={{ textAlign: 'center', mb: 5 }} data-aos="fade-up">
              <Typography
                variant="overline"
                sx={{
                  color: '#00796b',
                  fontWeight: 700,
                  letterSpacing: '0.18em',
                }}
              >
                GET IN TOUCH
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  mt: 1,
                  mb: 1.5,
                  fontSize: { xs: '2rem', md: '2.6rem' },
                  background: 'linear-gradient(135deg, #00796b 0%, #004d40 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Let's talk
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: '1.05rem',
                  lineHeight: 1.7,
                  maxWidth: 540,
                  mx: 'auto',
                }}
              >
                Have a question or want to learn more about Bixbite AI? Reach out
                to us anytime — our team typically replies within one business day.
              </Typography>
            </Box>
          </Fade>

          {/* Contact info cards */}
          <Grid container spacing={2.5} sx={{ mb: 4 }} data-aos="fade-up">
            {contactInfo.map((info, idx) => (
              <Grid item xs={12} sm={4} key={idx}>
                <Box
                  component="a"
                  href={info.href}
                  target={info.label === 'Address' ? '_blank' : undefined}
                  rel={info.label === 'Address' ? 'noopener noreferrer' : undefined}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    gap: 1.5,
                    p: 3,
                    borderRadius: '20px',
                    textDecoration: 'none',
                    bgcolor: '#f8fffe',
                    border: '1px solid rgba(0, 121, 107, 0.1)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: '0 16px 36px -12px rgba(0, 77, 64, 0.25)',
                      borderColor: 'rgba(0, 121, 107, 0.3)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '16px',
                      background: 'linear-gradient(135deg, #00796b 0%, #004d40 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      boxShadow: '0 8px 20px -8px rgba(0, 77, 64, 0.5)',
                      '& svg': { fontSize: 26 },
                    }}
                  >
                    {info.icon}
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#00796b',
                      fontWeight: 700,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      fontSize: '0.65rem',
                    }}
                  >
                    {info.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: '#1a2f2c', fontWeight: 600, lineHeight: 1.5 }}
                  >
                    {info.value}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Map */}
          <Fade in timeout={900}>
            <Box data-aos="fade-up">
              <Box
                sx={{
                  borderRadius: '20px',
                  overflow: 'hidden',
                  boxShadow: '0 8px 30px -12px rgba(0, 77, 64, 0.25)',
                  border: '1px solid rgba(0, 121, 107, 0.1)',
                  height: { xs: 260, md: 320 },
                }}
              >
                <iframe
                  title="Cohort Coworking, Kondapur, Hyderabad"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3806.0808918576295!2d78.36269341121218!3d17.45584198337401!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcb9366a7909207%3A0xa5ef612527c881f8!2sCOHORT%20Coworking%20%40%20Kondapur!5e0!3m2!1sen!2sin!4v1754069448956!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </Box>

              <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<MapIcon />}
                  onClick={() =>
                    window.open(GOOGLE_MAPS_URL, '_blank', 'noopener,noreferrer')
                  }
                  sx={{
                    py: 1.5,
                    px: 4,
                    fontWeight: 700,
                    fontSize: '1rem',
                    borderRadius: '50px',
                    textTransform: 'none',
                    color: 'white',
                    background: 'linear-gradient(135deg, #00897b 0%, #004d40 100%)',
                    boxShadow: '0 10px 28px -8px rgba(0, 77, 64, 0.45)',
                    transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: '0 16px 36px -8px rgba(0, 77, 64, 0.55)',
                    },
                  }}
                  aria-label="Get Directions"
                >
                  Get Directions
                </Button>
              </Box>
            </Box>
          </Fade>
        </Box>
      </Paper>
    </Box>
  );
};

export default Contact;
